import React, { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import '../styles/BillingForecast.scss';

interface BillingServiceItem {
    service: string;
    os_resource_id: string;
    actual_cost_so_far: number;
    current_hourly_rate: number;
    forecasted_remaining: number;
    forecasted_total: number;
    hours_active: number;
    metadata: any;
    is_active: boolean;
    recorded_at: string;
    ended_at: string | null;
}

interface BillingForecastProps {
    forecast: {
        policy_version?: number;
        summary: {
            actual_spent_this_month: number;
            forecasted_total_this_month: number;
            forecasted_remaining: number;
            subscription_fee?: number;
            paid_usage?: number;
        };
        active: BillingServiceItem[];
        inactive: BillingServiceItem[];
        credits: number;
        credits_source?: 'stripe' | 'trial' | 'ledger' | 'plan_ledger';
        credits_expires_at?: number | null;
        credits_expiry_soon?: boolean;
        tier?: string | null;
        billing_period?: {
            start: string;
            end: string;
            source: 'stripe' | 'trial' | 'plan_ledger';
        };
        egress_usage?: {
            total_bytes: string;
            chargeable_bytes: string;
            free_tier_bytes: string;
        } | null;
    };
    isHomepageView?: boolean;
    billingLink?: boolean;
    nextPaymentDue?: string | null;  // ISO date from Stripe subscription
    detailsDefaultOpen?: boolean;
    view?: 'full' | 'summary' | 'usage';
}

const formatMoney = (value: number, preservePrecision = false) => {
    const amount = Number.isFinite(value) ? value : 0;
    const fractionDigits = amount === 0 || (!preservePrecision && Math.abs(amount) >= 0.01) ? 2 : 4;

    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(amount);
};

const BillingForecast: React.FC<BillingForecastProps> = ({ forecast, isHomepageView = false, billingLink = false, nextPaymentDue, detailsDefaultOpen = false, view = 'full' }) => {
    const [isInactiveExpanded, setIsInactiveExpanded] = useState(false);

    if (forecast.policy_version === 2 || forecast.policy_version === 3) {
        // A plan account's usage in the console's usage cards and table (Milos, 2026-11-01: "Still looks bad"): what this period
        // counts toward the budget, the credits used before the card, then each resource's paid usage after credits.
        const feeInclusive=forecast.policy_version===3;
        const resources=[...forecast.active,...forecast.inactive];
        return <section className="billing-forecast billing-forecast--full billing-forecast--plan" aria-label="Net usage details">
            <div className="usage-group-grid">
                <div className="usage-group-card">
                    <div><span>{feeInclusive ? 'This period' : 'Additional spend'}</span></div>
                    <strong>{formatMoney(forecast.summary.actual_spent_this_month)}</strong>
                    <small>{feeInclusive
                        ? `${formatMoney(forecast.summary.subscription_fee ?? 0)} commitment + ${formatMoney(forecast.summary.paid_usage ?? 0)} paid usage, before taxes`
                        : 'Paid usage after credits, before taxes'}</small>
                </div>
                <div className="usage-group-card">
                    <div><span>Credits</span></div>
                    <strong>{formatMoney(forecast.credits ?? 0)}</strong>
                    <small>Used before your card</small>
                </div>
            </div>
            {resources.length ? <div className="table-container"><table className="forecast-table"><thead><tr><th>Resource</th><th>Paid usage</th><th>State</th></tr></thead>
                <tbody>{resources.map(resource=><tr key={`${resource.service}:${resource.os_resource_id}`}>
                    <td><div className="service-main">{resource.metadata?.name || resource.os_resource_id || resource.service}</div>
                        <span className="service-sub">{String(resource.service).replaceAll('_', ' ')}</span></td>
                    <td>{formatMoney(resource.actual_cost_so_far)}</td><td>{resource.metadata?.resource_state==='unknown'?'Unknown':resource.is_active?'Active':'Ended'}</td>
                </tr>)}</tbody></table></div>
                : <div className="empty-forecast"><p>No paid usage in this period yet.</p></div>}
        </section>;
    }

    if (isHomepageView) {
        const totalForecast = forecast.summary.forecasted_total_this_month;
        const rawCredits = forecast.credits || 0;
        // Stripe's available balance is already the billing authority for carded customers. Do not
        // subtract local, unfinalized usage from it: Stripe applies grants at invoice finalization.
        const creditsLeft = rawCredits;
        const hasCredits = rawCredits > 0;
        const hasActiveResources = forecast.active.length > 0 || forecast.inactive.length > 0;
        const expirySoon = forecast.credits_expiry_soon === true;
        const daysUntilExpiry = forecast.credits_expires_at
            ? Math.ceil((forecast.credits_expires_at * 1000 - Date.now()) / (1000 * 60 * 60 * 24))
            : null;

        const egress = forecast.egress_usage;
        const GB = 1024 ** 3;
        const TB = 1024 ** 4;
        // Always show the meter with a 2 TB free-tier baseline, even before any egress is recorded -
        // so it's a persistent, discoverable meter instead of vanishing at zero usage.
        const egressTotalBytes = egress ? Number(egress.total_bytes) : 0;
        const egressFreeBytes  = egress && Number(egress.free_tier_bytes) > 0 ? Number(egress.free_tier_bytes) : 2 * TB;
        const egressPct = Math.min(100, (egressTotalBytes / egressFreeBytes) * 100);
        const egressDisplay = egressTotalBytes >= TB
            ? `${(egressTotalBytes / TB).toFixed(2)} TB`
            : `${(egressTotalBytes / GB).toFixed(2)} GB`;
        const isTrial = (forecast as any).tier === 'trial_no_card';
        // Tier-aware egress free tier for display: no-card trials get a smaller allowance (default
        // 5 GB) than the 2 TB carded tier. Prefer the backend value when egress has been recorded;
        // otherwise fall back by tier so the label is right even before any usage.
        const egressFreeForLabel = egress && Number(egress.free_tier_bytes) > 0
            ? Number(egress.free_tier_bytes)
            : (isTrial ? 5 * GB : 2 * TB);
        const freeTierLabel = egressFreeForLabel >= TB
            ? `${(egressFreeForLabel / TB).toFixed(0)} TB`
            : `${(egressFreeForLabel / GB).toFixed(0)} GB`;

        return (
            <div className="billing-forecast homepage-view">
                <div className="homepage-forecast-grid">
                    <div className="homepage-forecast-cell homepage-forecast-cell--primary">
                            <span>{hasActiveResources ? 'Estimated this period' : 'Period estimate'}</span>
                        <strong>
                            {formatMoney(totalForecast)}
                            {totalForecast > 0 && <small>/mo</small>}
                        </strong>
                        {!hasActiveResources && (
                            <p>No active resources yet.</p>
                        )}
                    </div>
                    <div className={`homepage-forecast-cell${expirySoon ? ' homepage-forecast-cell--warning' : ''}`}>
                        <span>{isTrial ? 'Trial credits' : 'Credits'}</span>
                        <strong>{formatMoney(hasCredits ? creditsLeft : 0)}</strong>
                        {forecast.credits_expires_at ? (
                            <p>
                                {expirySoon && daysUntilExpiry !== null ? (
                                    <>Expires in <b>{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</b></>
                                ) : (
                                    <>Expires <b>{new Date(forecast.credits_expires_at * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</b></>
                                )}
                            </p>
                        ) : (
                            <p>Applied before card billing.</p>
                        )}
                    </div>
                </div>

                <div className="homepage-egress-row">
                    <div>
                        <span>Network egress</span>
                        <strong>
                            {egress ? `${egressDisplay} / ${freeTierLabel} free` : `No usage / ${freeTierLabel} free`}
                        </strong>
                    </div>
                    {egressTotalBytes > egressFreeBytes && <b>Over limit</b>}
                </div>
                <div className={`homepage-egress-meter${egressTotalBytes > egressFreeBytes ? ' homepage-egress-meter--warning' : ''}`}>
                    <div style={{ width: `${egressPct}%` }} />
                </div>

                {billingLink && (
                    <a href="/category/user/billing" className="homepage-forecast-link">
                        View billing details
                    </a>
                )}
            </div>
        );
    }

    const activeNonEgress = forecast.active.filter(svc => svc.service !== 'egress');
    const inactiveNonEgress = forecast.inactive.filter(svc => svc.service !== 'egress');
    const activeEgressEntry = forecast.active.find(svc => svc.service === 'egress');

    const totalCurrentHourlyRate = activeNonEgress.reduce(
        (sum, svc) => sum + svc.current_hourly_rate,
        0
    );

    // Egress: actual cost is stored in current_hourly_rate, not actual_cost_so_far
    const egressCost = activeEgressEntry?.current_hourly_rate ?? 0;
    const activeNonEgressSpent = activeNonEgress.reduce((sum, svc) => sum + svc.actual_cost_so_far, 0);
    const inactiveNonEgressSpent = inactiveNonEgress.reduce((sum, svc) => sum + svc.actual_cost_so_far, 0);
    const adjustedSpentSoFar = activeNonEgressSpent + inactiveNonEgressSpent + egressCost;
    const adjustedForecastedTotal = forecast.summary.forecasted_total_this_month
        - (activeEgressEntry?.actual_cost_so_far ?? 0)
        + egressCost;
    const adjustedForecastedRemaining = Math.max(0, adjustedForecastedTotal - adjustedSpentSoFar);
    const creditsRemaining = forecast.credits || 0;
    const estimatedExtraCardCharge = Math.max(0, adjustedForecastedRemaining - creditsRemaining);
    const hasActiveResources = activeNonEgress.length > 0 || !!activeEgressEntry;

    // Stripe supplies the item period through the forecast. No carded UI date is derived from the
    // browser calendar, because subscriptions can renew on any day of the month.
    const now = new Date();
    const billingPeriod = forecast.billing_period ?? null;
    const isStripePeriod = billingPeriod?.source === 'stripe';
    const periodStart = billingPeriod ? new Date(billingPeriod.start) : null;
    const periodEnd = billingPeriod ? new Date(billingPeriod.end) : null;
    const hasBillingPeriod = !!periodStart && !!periodEnd && !Number.isNaN(periodStart.getTime()) && !Number.isNaN(periodEnd.getTime());
    const daysLeft = periodEnd ? Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : null;
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const fmtFull = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    const detailedExpirySoon = forecast.credits_expiry_soon === true && (forecast.credits || 0) > 0;
    const detailedDaysUntilExpiry = forecast.credits_expires_at
        ? Math.ceil((forecast.credits_expires_at * 1000 - Date.now()) / (1000 * 60 * 60 * 24))
        : null;

    const eu = forecast.egress_usage;
    const TB = 1024 ** 4;
    const GB = 1024 ** 3;
    const isTrial = forecast.tier === 'trial_no_card';
    const freeTierBytes = eu && Number(eu.free_tier_bytes) > 0 ? Number(eu.free_tier_bytes) : (isTrial ? 5 * GB : 2 * TB);
    const freeTierDisplay = freeTierBytes >= TB ? `${(freeTierBytes / TB).toFixed(0)} TB` : `${(freeTierBytes / GB).toFixed(0)} GB`;
    // This month's usage from the active egress entry's metadata if present, else the aggregate.
    const monthTotalBytes = activeEgressEntry?.metadata?.total_egress_bytes != null
        ? Number(activeEgressEntry.metadata.total_egress_bytes)
        : (eu ? Number(eu.total_bytes) : 0);
    const monthUsedDisplay = monthTotalBytes >= TB
        ? `${(monthTotalBytes / TB).toFixed(2)} TB`
        : `${(monthTotalBytes / GB).toFixed(2)} GB`;
    const chargeableGB = activeEgressEntry?.metadata?.chargeable_gb ?? (eu ? Number(eu.chargeable_bytes) / GB : 0);
    const overLimit = monthTotalBytes > freeTierBytes;
    const hasEgressData = !!eu;
    const egressPct = Math.min(100, (monthTotalBytes / freeTierBytes) * 100);

    const serviceGroupLabels: Record<string, string> = {
        instance: 'Compute',
        volume: 'Volumes',
        snapshot: 'Snapshots',
        backup: 'Backups',
        network: 'Networking',
        floating_ip: 'Networking',
        load_balancer: 'Load balancers',
        database: 'Managed databases',
        redis: 'Managed Redis',
    };

    const groupedActiveUsage = activeNonEgress.reduce<Record<string, { spent: number; hourly: number; forecast: number; count: number }>>((groups, svc) => {
        const label = serviceGroupLabels[svc.service] || svc.service.replaceAll('_', ' ');
        const current = groups[label] || { spent: 0, hourly: 0, forecast: 0, count: 0 };
        groups[label] = {
            spent: current.spent + svc.actual_cost_so_far,
            hourly: current.hourly + svc.current_hourly_rate,
            forecast: current.forecast + svc.forecasted_total,
            count: current.count + 1,
        };
        return groups;
    }, {});
    const groupedUsageRows = Object.entries(groupedActiveUsage);
    const inactiveSpent = inactiveNonEgress.reduce((sum, svc) => sum + svc.actual_cost_so_far, 0);
    const periodLabel = isStripePeriod ? 'billing period' : isTrial ? 'trial period' : 'current period';
    const billingExplanation = isTrial
        ? 'You are using the trial. A card is only needed for capabilities that require one.'
        : estimatedExtraCardCharge > 0
            ? `${formatMoney(estimatedExtraCardCharge)} is estimated after applicable credits, based on resources currently running.`
            : creditsRemaining > 0
                ? 'Applicable credits are used before any card charge.'
                : 'No extra card charge is estimated from your current usage.';
    const estimatedChargeLabel = isTrial ? 'Estimated paid usage' : 'Estimated card charge';
    const estimatedChargeNote = isTrial ? 'If your account is upgraded' : 'After applicable credits';

    return (
        <div className="billing-forecast billing-forecast--full">
            {view !== 'usage' && <>
            <div className="billing-hero">
                <div className="billing-hero__main">
                    <p className="billing-kicker">{isTrial ? 'Current trial usage' : 'Current billing overview'}</p>
                    <h3>{formatMoney(adjustedForecastedTotal)} estimated this {periodLabel}</h3>
                    <p className="subtitle">
                        {formatMoney(adjustedSpentSoFar, true)} spent so far · {formatMoney(adjustedForecastedRemaining, true)} forecasted remaining{hasBillingPeriod && daysLeft !== null ? ` · ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left` : ''}
                    </p>
                </div>
                <div className="billing-period-meta">
                    {hasBillingPeriod && periodStart && periodEnd && <div>{isStripePeriod ? 'Billing period' : 'Trial period'} <strong>{fmt(periodStart)} - {fmt(periodEnd)}</strong></div>}
                    {nextPaymentDue && <div>Next payment <strong>{fmtFull(new Date(nextPaymentDue))}</strong></div>}
                </div>
            </div>
            <p className="billing-charge-explanation">{billingExplanation}</p>

            <div className="billing-summary-grid">
                <div className="billing-summary-card billing-summary-card--estimate">
                    <span>{estimatedChargeLabel}</span>
                    <strong>{formatMoney(estimatedExtraCardCharge)}</strong>
                    <small>{estimatedChargeNote}</small>
                </div>
                <div className="billing-summary-card">
                    <span>Credits remaining</span>
                    <strong>{formatMoney(creditsRemaining)}</strong>
                </div>
                <div className="billing-summary-card">
                    <span>Spent this period</span>
                    <strong>{formatMoney(adjustedSpentSoFar, true)}</strong>
                </div>
                <div className="billing-summary-card">
                    <span>Current hourly rate</span>
                    <strong>{formatMoney(totalCurrentHourlyRate, true)}</strong>
                </div>
            </div>

            </>}

            {view !== 'usage' && !hasActiveResources && inactiveNonEgress.length === 0 && (
                <div className="empty-forecast">
                    <p>No active resources yet. Your usage and forecasts will appear after your first deployment.</p>
                    <a className="empty-forecast__cta" href="/deploy">Deploy infrastructure</a>
                </div>
            )}

            {view !== 'summary' && <>
            <div className="usage-group-grid">
                {groupedUsageRows.map(([label, totals]) => (
                    <div className="usage-group-card" key={label}>
                        <div>
                            <span>{label}</span>
                            <small>{totals.count} active resource{totals.count !== 1 ? 's' : ''}</small>
                        </div>
                        <strong>{formatMoney(totals.forecast, true)}</strong>
                        <small>{formatMoney(totals.spent, true)} spent · {formatMoney(totals.hourly, true)}/hr</small>
                    </div>
                ))}
                <div className={`usage-group-card usage-group-card--egress${overLimit ? ' usage-group-card--warning' : ''}`}>
                    <div>
                        <span>Network egress</span>
                        <small>{hasEgressData ? `${monthUsedDisplay} used / ${freeTierDisplay} free` : `No usage recorded · ${freeTierDisplay} free`}</small>
                    </div>
                    <strong>{formatMoney(egressCost, true)}</strong>
                    <small>{Number(chargeableGB).toLocaleString('en-GB', { maximumFractionDigits: 2 })} GB chargeable{overLimit ? ' · over included allowance' : ''}</small>
                    <div className="usage-meter">
                        <div style={{ width: `${egressPct}%` }} />
                    </div>
                </div>
                {inactiveNonEgress.length > 0 && (
                    <div className="usage-group-card usage-group-card--muted">
                        <div>
                            <span>Deleted resources</span>
                            <small>{inactiveNonEgress.length} inactive resource{inactiveNonEgress.length !== 1 ? 's' : ''}</small>
                        </div>
                        <strong>{formatMoney(inactiveSpent, true)}</strong>
                        <small>Past usage still counts for this period.</small>
                    </div>
                )}
            </div>

            {(forecast as any).tier !== 'trial_no_card' && detailedExpirySoon && forecast.credits_expires_at && detailedDaysUntilExpiry !== null && (
                <div className="credits-expiry-banner">
                    <span className="credits-expiry-banner__icon">⚠</span>
                    <span>
                        Your credits ({formatMoney(forecast.credits || 0)}) expire in{' '}
                        <strong>{detailedDaysUntilExpiry} day{detailedDaysUntilExpiry !== 1 ? 's' : ''}</strong>{' '}
                        on {new Date(forecast.credits_expires_at * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
                    </span>
                </div>
            )}

            <details className="usage-details" open={detailsDefaultOpen}>
                <summary>
                    <span>Usage details</span>
                    <small>Resource-level rows, hourly rates, and deleted resources.</small>
                </summary>
            <div className="table-container">
                <table className="forecast-table">
                    <thead>
                    <tr>
                        <th>Service</th>
                        <th>Started At</th>
                        <th>Ended At</th>
                        <th>Spent So Far</th>
                        <th>Hourly Rate</th>
                        <th>Est. Total This Period</th>
                    </tr>
                    </thead>
                    <tbody>
                    {/* Active Resources */}
                    {activeNonEgress.map((svc) => (
                        <tr key={svc.os_resource_id || svc.service}>
                            <td>
                                <div className="service-main">
                                    {svc.metadata?.name || svc.service}
                                    {svc.service === 'instance' && svc.metadata?.flavor && ` (${svc.metadata.flavor})`}
                                </div>
                                <span className="service-sub">{svc.service.replaceAll('_', ' ')}</span>
                            </td>
                            <td>{new Date(svc.recorded_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                            <td>{svc.ended_at ? new Date(svc.ended_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : "Active"}</td>
                            <td>{formatMoney(svc.actual_cost_so_far, true)}</td>
                            <td>{formatMoney(svc.current_hourly_rate, true)}</td>
                            <td>{formatMoney(svc.forecasted_total, true)}</td>
                        </tr>
                    ))}

                    {/* Inactive Summary Row / Tab */}
                    {inactiveNonEgress.length > 0 && (
                        <>
                            <tr
                                className="inactive-summary-row"
                                onClick={() => setIsInactiveExpanded(!isInactiveExpanded)}
                                style={{ cursor: 'pointer', backgroundColor: 'var(--bg-table)' }}
                            >
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {isInactiveExpanded ? <FaChevronUp size={12}/> : <FaChevronDown size={12}/>}
                                        <strong>Inactive Resources ({inactiveNonEgress.length})</strong>
                                    </div>
                                </td>
                                <td></td>
                                <td></td>
                                <td><strong>{formatMoney(inactiveNonEgress.reduce((s, sv) => s + sv.actual_cost_so_far, 0), true)}</strong></td>
                                <td>{formatMoney(0, true)}</td>
                                <td><strong>{formatMoney(inactiveNonEgress.reduce((s, sv) => s + sv.actual_cost_so_far, 0), true)}</strong></td>
                            </tr>

                            {/* Expanded Inactive Details */}
                            {isInactiveExpanded && inactiveNonEgress.map((svc) => (
                                <tr key={svc.os_resource_id || svc.service} className="inactive-detail-row">
                                    <td style={{ paddingLeft: '32px' }}>
                                        <div className="service-main">
                                            {svc.metadata?.name || svc.service}
                                            {svc.service === 'instance' && svc.metadata?.flavor && ` (${svc.metadata.flavor})`}
                                        </div>
                                        <span className="service-sub">{svc.service.replaceAll('_', ' ')} (Deleted)</span>
                                    </td>
                                    <td>{new Date(svc.recorded_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                                    <td>{svc.ended_at ? new Date(svc.ended_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
                                    <td>{formatMoney(svc.actual_cost_so_far, true)}</td>
                                    <td>{formatMoney(0, true)}</td>
                                    <td>{formatMoney(svc.actual_cost_so_far, true)}</td>
                                </tr>
                            ))}
                        </>
                    )}
                    </tbody>
                    <tfoot>
                    <tr className="totals-row">
                        <td><strong>Total Forecast</strong></td>
                        <td></td>
                        <td></td>
                        <td><strong>{formatMoney(adjustedSpentSoFar, true)}</strong></td>
                        <td><strong>{formatMoney(totalCurrentHourlyRate, true)}</strong></td>
                        <td><strong>{formatMoney(adjustedForecastedTotal, true)}</strong></td>
                    </tr>
                    </tfoot>
                </table>
            </div>
            </details>
            </>}
        </div>
    );
};

export default BillingForecast;
